import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCurrentUser } from './useAuth';

export interface Mobilisation {
  id: string;
  name: string;
  date: string;
  status: 'planning' | 'active' | 'closed';
  kind: 'mobilisation' | 'onboarding';
  day_label: string | null;
  created_at: string;
}

export interface MobilisationInput {
  name: string;
  date: string;
  status?: 'planning' | 'active' | 'closed';
}

const LIST_KEY = ['mobilisations'] as const;

export function useMobilisationsList() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async () =>
      (await api<{ mobilisations: Mobilisation[] }>('/api/mobilisations'))
        .mobilisations,
  });
}

export function useCreateMobilisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MobilisationInput) =>
      api<{ mobilisation: Mobilisation }>('/api/mobilisations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useUpdateMobilisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & Partial<MobilisationInput>) =>
      api<{ mobilisation: Mobilisation }>(`/api/mobilisations/${input.id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteMobilisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/mobilisations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/**
 * The mobilisation the session is currently scoped to, or undefined while
 * loading / when the viewer is on the cross-mobilisation "All" view. Lets
 * screens adapt to the scope's kind (a day mobilisation vs the ongoing
 * onboarding programme).
 */
export function useCurrentScope(): Mobilisation | undefined {
  const { data: user } = useCurrentUser();
  const list = useMobilisationsList();
  const id = user?.current_mobilisation_id;
  if (!id) return undefined;
  return (list.data ?? []).find((m) => m.id === id);
}

export function useSwitchMobilisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mobilisation_id: string | null) =>
      api<{ current_mobilisation_id: string | null }>(
        '/api/mobilisations/_switch',
        { method: 'POST', body: JSON.stringify({ mobilisation_id }) },
      ),
    onSuccess: () => {
      // Invalidate everything that depends on mobilisation scope
      qc.invalidateQueries();
    },
  });
}
